// How Account and the admin user drawer word an API key or a connected app (Claude, ChatGPT).
// docs/plans/claude-connector.md §5. Plain sentences; no machine vocabulary reaches a person.

import type { CredentialScope, CredentialSummary } from '@/api/types';

/**
 * The lifetimes a key may be created with, in days: the mirror of
 * `shared::API_KEY_LIFETIMES_DAYS`, which the API checks and publishes as the enum of
 * `CreateApiKeyRequest.expires_in_days` in openapi.json. `credentials.test.ts` holds the two
 * equal, so a lifetime the API refuses is never offered.
 */
export const API_KEY_LIFETIMES_DAYS = [30, 90, 180, 365] as const;
export type ApiKeyLifetimeDays = (typeof API_KEY_LIFETIMES_DAYS)[number];
export const DEFAULT_API_KEY_LIFETIME_DAYS: ApiKeyLifetimeDays = 90;

/** The longest key name the API accepts, after trimming. */
export const API_KEY_NAME_MAX = 100;

/** What a credential may do, as a person reads it. */
export const ACCESS_LABEL: Record<CredentialScope, string> = {
  read: 'Read',
  write: 'Read & edit',
  prepare: 'Read, edit & prepare',
  admin: 'Administer',
};

export const ACCESS_BLURB: Record<CredentialScope, string> = {
  read: 'See campaigns, audiences, creatives and reports.',
  write: 'Also create and edit draft campaigns and creatives. Never launches, pauses or changes anything live.',
  prepare:
    'Also create new audiences (SMART starts uploading them to Meta and The Trade Desk; nothing is spent) and submit creatives for review. Never approves, launches, pauses or changes anything live.',
  admin:
    'Also change settings (never its own access or any secret), handle alerts, run remediations and invoice actions, and manage users. A setting it changes takes effect on live campaigns. SMART never shows it an email address, and it never launches, pauses or approves a campaign or a creative.',
};

/** The scopes in increasing order: each covers every scope before it. */
const SCOPE_ORDER: readonly CredentialScope[] = ['read', 'write', 'prepare', 'admin'];

/** Whether a credential holding `held` may do what `needed` allows. */
export function scopeCovers(held: CredentialScope, needed: CredentialScope): boolean {
  return SCOPE_ORDER.indexOf(held) >= SCOPE_ORDER.indexOf(needed);
}

/** The scopes a person may give a new key, up to their ceiling (`max_scope`). */
export function issuableScopes(maxScope: CredentialScope): readonly CredentialScope[] {
  return SCOPE_ORDER.filter((scope) => scopeCovers(maxScope, scope));
}

/** Whose screen the list is on: the owner's own, or an admin looking at someone's. */
export type CredentialViewer = 'owner' | 'admin';

/** Revoked or expired: no request it carries is answered any more. */
export function credentialEnded(credential: CredentialSummary, now: number = Date.now()): boolean {
  return credential.revoked_at != null || +new Date(credential.expires_at) <= now;
}

/** Why a revoked credential stopped working, in words; `revoke_reason` is a stored code. */
export function revokedReason(reason: string | null, viewer: CredentialViewer): string {
  switch (reason) {
    case 'owner':
      return viewer === 'owner' ? 'You revoked it' : 'Revoked by its owner';
    case 'admin':
      return 'Revoked by an admin';
    case 'user_disabled':
      return 'Revoked when the account was disabled';
    case 'user_deleted':
      return 'Revoked when the account was deleted';
    case 'role_changed':
      return 'Revoked when the role changed';
    case 'password_reset':
      return 'Revoked when the password was reset';
    case 'refresh_reuse':
      return 'Revoked because its sign-in was used twice, which can mean it was copied';
    case 'code_replay':
      return 'Revoked because its sign-in code was used twice';
    case 'client':
      return 'Revoked by the app itself';
    case 'code_unused':
      return 'Revoked because the app never finished signing in';
    default:
      return 'Revoked';
  }
}

/** The `curl` line that tries a key, against the API the app itself calls. */
export function curlExample(apiBase: string, origin: string): string {
  const url = new URL(`${apiBase.replace(/\/$/, '')}/me`, origin).href;
  return `curl -H "Authorization: Bearer <key>" ${url}`;
}

/**
 * The connector's address: `/mcp` at the root of the host that serves the API
 * (`https://batch.shubertads.com/mcp` in production), the one URL Claude, ChatGPT and
 * Claude Code are given.
 */
export function connectorUrl(apiBase: string, origin: string): string {
  return new URL('/mcp', new URL(apiBase || '/', origin)).href;
}

/**
 * The first message Account suggests in Claude or ChatGPT, so "SMART" means this connector in
 * every later chat (Gio, 2026-11-02).
 */
export const CONNECTOR_FIRST_PROMPT =
  'Please remember: SMART refers to the connected SMART advertising platform. Use its tools whenever I ask about SMART.';

/** The line that adds the connector to Claude Code with an API key (docs/mcp.md). */
export function claudeCodeCommand(connector: string): string {
  return `claude mcp add --transport http smart ${connector} --header "Authorization: Bearer <key>"`;
}
